import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ensureSalesReporterSession } from "@/lib/salesReporterSso";
import { IdCard, Route as RouteIcon, ChevronRight } from "lucide-react";

// Dashboard cards with live figures from the two connected apps, fetched
// through their relays (backend/hr_bridge.py, backend/sales_app_bridge.py)
// with the viewer's OWN sign-in to each app — HR's session cookie and the
// Sales Reporter's sr_token, obtained by single sign-on when it is set up —
// see lib/salesReporterSso.js. No app password is stored in the ERP, and each
// app decides what this person may see: a card shows only what their HR /
// Sales Reporter permissions allow, a sign-in prompt if they aren't signed in
// there yet, and nothing at all if the app isn't connected or they have no
// access.

// Calls into the relays go through fetch, not the shared axios client: that
// client signs the person out of the ERP on any 401, and "not signed in to
// HR / the Sales Reporter" is a 401 from those apps, not from the ERP.
async function relayGet(path, params = {}, headers = {}) {
  const u = new URL(`${API_BASE}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
  let erpToken = "";
  try { erpToken = localStorage.getItem("token") || ""; } catch { /* storage blocked */ }
  const res = await fetch(u, {
    credentials: "include",
    headers: { Accept: "application/json", ...(erpToken ? { Authorization: `Bearer ${erpToken}` } : {}), ...headers },
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  return { status: res.status, data };
}

const inr = (v) => `₹${Math.round(Number(v) || 0).toLocaleString("en-IN")}`;
const num = (v) => (Number(v) || 0).toLocaleString("en-IN");

function Card({ icon: Icon, title, to, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>
          <Icon className="w-4 h-4 text-blue-600" /> {title}
        </div>
        <Link to={to} className="text-xs text-blue-700 hover:underline flex items-center gap-0.5">Open <ChevronRight className="w-3 h-3" /></Link>
      </div>
      {children}
    </div>
  );
}

function Figures({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {items.filter(Boolean).map(([label, value]) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
          <div className="text-lg font-bold text-slate-900 mono">{value}</div>
        </div>
      ))}
    </div>
  );
}

const SignIn = ({ to, app }) => (
  <p className="text-sm text-slate-600">
    Sign in to {app} to see its figures here. <Link to={to} className="text-blue-700 hover:underline">Open {app}</Link>
  </p>
);

export function HrSummaryCard() {
  const [state, setState] = useState({ kind: "loading" });
  useEffect(() => {
    let alive = true;
    relayGet("/hr/app/api/index.php", { a: "adm.dashboard" })
      .then(({ status, data }) => {
        if (!alive) return;
        if (status === 200 && data?.ok) setState({ kind: "ok", d: data.data || {} });
        else setState({ kind: status === 401 ? "signin" : "hidden" });   // 403 employee / 409 not connected: no card
      })
      .catch(() => alive && setState({ kind: "hidden" }));
    return () => { alive = false; };
  }, []);
  if (state.kind === "hidden" || state.kind === "loading") return null;
  const d = state.d || {};
  const att = d.attendance || {};
  const today = att.today || {};
  // HR attendance codes: P present, OD on duty, HD half day (A absent, OFF / H holiday).
  const present = ["P", "OD", "HD"].reduce((a, k) => a + (Number(today[k]) || 0), 0);
  const pendingReq = Object.values(d.requests_pending || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  return (
    <Card icon={IdCard} title="HR" to="/hr" testid="dash-hr-card">
      {state.kind === "signin" ? <SignIn to="/hr" app="HR" /> : (
        <Figures items={[
          ["Active employees", num(d.employees?.active ?? d.employees?.total)],
          d.attendance && ["Present today", num(present)],
          d.attendance && ["On leave today", num(att.today_on_leave)],
          d.leave && ["Leave pending", num(d.leave.pending)],
          ["Requests pending", num(pendingReq)],
          d.payroll && [`Net payroll ${d.payroll.month || ""}`.trim(), inr(d.payroll.net)],
        ]} />
      )}
    </Card>
  );
}

export function SalesSummaryCard() {
  const { user } = useAuth();
  const [state, setState] = useState({ kind: "loading" });
  useEffect(() => {
    let alive = true;
    const storedToken = () => { try { return localStorage.getItem("sr_token") || ""; } catch { return ""; } };
    api.get("/sales-app/status").then(async ({ data: st }) => {
      if (!st?.configured) return alive && setState({ kind: "hidden" });
      // Single sign-on: get (or reuse) this ERP user's own Sales Reporter session.
      if (st.sso && user) await ensureSalesReporterSession(user);
      let token = storedToken();
      if (!alive) return;
      if (!token) return setState({ kind: "signin" });
      const call = (file) => relayGet(`/sales-app/app/api/${file}`, { period: "monthly" }, { "X-Session-Token": token });
      let team = await call("get_team_summary.php");
      if (team.status === 401 && st.sso && user) {            // stored session expired: renew once
        await ensureSalesReporterSession(user, { force: true });
        token = storedToken();
        if (token) team = await call("get_team_summary.php");
      }
      if (!alive) return;
      if (team.status === 401) return setState({ kind: "signin" });
      if (team.status === 200 && team.data?.ok) {
        return setState({ kind: "ok", team: true, t: team.data.totals || {}, members: (team.data.members || []).length });
      }
      const mine = await call("get_my_summary.php");   // not a manager: own figures
      if (!alive) return;
      if (mine.status === 401) return setState({ kind: "signin" });
      if (mine.status === 200 && mine.data?.ok) setState({ kind: "ok", team: false, t: { ...(mine.data.visits || {}), ...(mine.data.expenses || {}) } });
      else setState({ kind: "hidden" });
    }).catch(() => alive && setState({ kind: "hidden" }));
    return () => { alive = false; };
  }, [user]);
  if (state.kind === "hidden" || state.kind === "loading") return null;
  const t = state.t || {};
  return (
    <Card icon={RouteIcon} title={state.team ? `Sales — this month (team of ${state.members})` : "Sales — this month"} to="/sales-report" testid="dash-sales-card">
      {state.kind === "signin" ? <SignIn to="/sales-report" app="the Sales Reporter" /> : (
        <Figures items={[
          ["Visits", num(t.total_visits)],
          ["Quotations", `${num(t.quotations_sent)} · ${inr(t.quotation_value)}`],
          ["Orders", `${num(t.orders_received)} · ${inr(t.order_value)}`],
          ["Payment collected", inr(t.payment_collected)],
          ["Distance", `${num(Math.round(Number(t.total_km) || 0))} km`],
          ["Expenses", inr(t.total_expenses)],
        ]} />
      )}
    </Card>
  );
}

export default function ConnectedAppsSummary() {
  return (
    <div className="grid lg:grid-cols-2 gap-4 empty:hidden" data-testid="dash-connected-apps">
      <HrSummaryCard />
      <SalesSummaryCard />
    </div>
  );
}
